import { useState } from 'react';

export function ValuationHero() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    numberOfUnits: '',
    currentOccupancy: '',
    monthlyRent: '',
    propertyCondition: '',
    timeline: '',
    additionalInfo: ''
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'additionalInfo') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qoadc98t1oshnre0d1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          numberOfUnits: '',
          currentOccupancy: '',
          monthlyRent: '',
          propertyCondition: '',
          timeline: '',
          additionalInfo: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center py-20 px-4">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/7fde0956031ac93bd0179357f29a9b74.jpg')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              How Much Is My Apartment Building Worth?
            </h1>
            <p className="text-xl lg:text-2xl mb-8 leading-relaxed">
              Get an accurate valuation of your apartment building using professional investor methods. Learn the 3 primary valuation approaches and quick estimation techniques.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-400 mt-1"></i>
                <p className="text-lg">Income approach, sales comparison, and cost approach explained</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-400 mt-1"></i>
                <p className="text-lg">Quick valuation methods: price per unit, cap rate, and GRM</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-400 mt-1"></i>
                <p className="text-lg">Honest as-is condition assessment and impact on value</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-400 mt-1"></i>
                <p className="text-lg">Free property valuation consultation with experienced investors</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Get Your Free Property Valuation</h2>
            <p className="text-gray-600 mb-6">Fill out the form below and receive a professional valuation estimate within 24 hours</p>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="valuation-request-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700 mb-1">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="numberOfUnits" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Units *
                  </label>
                  <input
                    type="number"
                    id="numberOfUnits"
                    name="numberOfUnits"
                    value={formData.numberOfUnits}
                    onChange={handleChange}
                    required
                    min="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="12"
                  />
                </div>
                <div>
                  <label htmlFor="currentOccupancy" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Occupancy *
                  </label>
                  <select
                    id="currentOccupancy"
                    name="currentOccupancy"
                    value={formData.currentOccupancy}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select occupancy</option>
                    <option value="100%">100% Occupied</option>
                    <option value="90-99%">90-99% Occupied</option>
                    <option value="75-89%">75-89% Occupied</option>
                    <option value="50-74%">50-74% Occupied</option>
                    <option value="Below 50%">Below 50% Occupied</option>
                    <option value="Vacant">Completely Vacant</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="monthlyRent" className="block text-sm font-medium text-gray-700 mb-1">
                    Total Monthly Rent *
                  </label>
                  <input
                    type="text"
                    id="monthlyRent"
                    name="monthlyRent"
                    value={formData.monthlyRent}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="$15,000"
                  />
                </div>
                <div>
                  <label htmlFor="propertyCondition" className="block text-sm font-medium text-gray-700 mb-1">
                    Property Condition *
                  </label>
                  <select
                    id="propertyCondition"
                    name="propertyCondition"
                    value={formData.propertyCondition}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select condition</option>
                    <option value="Excellent">Excellent - Recently renovated</option>
                    <option value="Good">Good - Well maintained</option>
                    <option value="Average">Average - Some updates needed</option>
                    <option value="Fair">Fair - Needs repairs</option>
                    <option value="Poor">Poor - Significant repairs needed</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
                  When Are You Looking to Sell? *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select timeline</option>
                  <option value="ASAP">As Soon As Possible</option>
                  <option value="1-3 months">Within 1-3 Months</option>
                  <option value="3-6 months">Within 3-6 Months</option>
                  <option value="6-12 months">Within 6-12 Months</option>
                  <option value="Just exploring">Just Exploring Options</option>
                </select>
              </div>

              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us more about your property, any specific concerns, or questions you have..."
                />
                <p className="text-xs text-gray-500 mt-1">{charCount}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="w-full bg-teal-600 text-white py-3 px-6 rounded-md font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitStatus === 'submitting' ? 'Submitting...' : 'Get My Free Valuation'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
                  Thank you! We'll contact you within 24 hours with your property valuation.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                  Something went wrong. Please try again or call us at (555) 123-4567.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}